import * as R from 'ramda'

import { Action } from '../actions/types'

import {
  SelectableItem,
  EditableBeam,
  EditableSlab,
  EditableLoad,
  EditableLoadCase,
  EditableCase
} from './projects/projectTypes'
import { findActiveCategory } from './projects/projectSelectors'
import { LocationChangeAction, LOCATION_CHANGE } from 'react-router-redux'

import {
  getSpaceType,
  getDefaultSpaceType
} from '../location_changes/conversionHelper'

// Export all selectors from here for easier access
export * from './projects/projectSelectors'

export {
  SelectableItem,
  EditableBeam,
  EditableSlab,
  EditableLoad,
  EditableLoadCase,
  EditableCase
}

/*
  If you need project information outside the workspace view, read it from Apollo. Redux
  usage should be limited to the workspace view editor only.

  This way we always have a single source of truth for data -> redux in the workspace view,
  Apollo everywhere else.

  The rationale for using redux at all is that the editor may have reasonably complicated
  interactions between elements so managing it with Apollo would be cumbersome.

  IOW; if you are introducing new properties to the state object, ask yourself - is this
  data something the user will edit in the workspace view? Note that you do not have to
  "pass through" data, our save will always reload latest content from server-side which
  will automatically keep the Apollo store up to date.
*/
export type ProjectState = {
  readonly projectId?: string

  readonly name: string
  readonly description: string

  // Currently only used for "load" errors
  readonly projectError?: string

  readonly cases: EditableCase[]
  // new is true when the current workspace is not part of a saved project
  readonly new: boolean
  // true if user has touched any form fields
  readonly dirty: boolean

  // Project location, default space type and temporary thumbnail
  readonly location: string
  readonly defaultSpaceType: string
  readonly defaultFireResistance: string
  readonly defaultConcreteGrade: string
  readonly thumbnailData: string

  // Selections
  activeCase: string | null
}

// Normally, the user shouldn't have to touch location and space type after the start
// so we mark them touched by default
const DefaultTouchedCategories = ['location', 'spacetype']

// Create an empty case (to which we normally load default values)
function createEmptyCase(spaceType: string): EditableCase {
  return {
    id: 'new_case',
    name: 'New case',
    description: '',
    spaceType,
    beams: [],
    slabs: [],
    loadCases: [],
    touchedCategories: DefaultTouchedCategories,
    _new: true
  }
}

const initialState: ProjectState = {
  projectId: undefined,
  new: true,
  description: '',
  dirty: false,
  name: 'Untitled',
  location: 'FI',
  defaultSpaceType: 'A',
  // Hopefully, one day we'll be smarter about these defaults
  defaultFireResistance: 'R15',
  defaultConcreteGrade: 'C25/30',
  cases: [createEmptyCase('A')],
  activeCase: 'new_case',
  thumbnailData: ''
}

/*

 State update helpers

*/

// Update a beam by its ID
function updateBeam(beam: EditableBeam) {
  return function(kase: EditableCase): EditableCase {
    const beamIndex = R.findIndex(R.propEq('id', beam.id), kase.beams)
    if (beamIndex === -1) {
      return kase
    }

    return {
      ...kase,
      beams: R.update(beamIndex, beam, kase.beams)
    }
  }
}

// Update a slab by its ID
function updateSlab(slab: EditableSlab) {
  return function(kase: EditableCase): EditableCase {
    const slabIndex = R.findIndex(R.propEq('id', slab.id), kase.slabs)
    if (slabIndex === -1) {
      return kase
    }

    /* All slabs must share top concrete value for now. Our slab tops always align
       currently so the beam and the slabs must have same amount of concrete
       on top. */
    const slabs = kase.slabs.map(s => ({ ...s, topConcrete: slab.topConcrete }))

    return { ...kase, slabs: R.update(slabIndex, slab, slabs) }
  }
}

// Painfully verbose name, but does what it says on the tin. Updates a beam and toggles
// (enabled/disabled) specific slabs.
function updateBeamAndToggleSlabs(
  beam: EditableBeam,
  toggles: { [key: string]: boolean }
) {
  return function(kase: EditableCase): EditableCase {
    let updated = updateBeam(beam)(kase)

    const slabs = kase.slabs.map(slab => {
      if ((R.has(slab.name), toggles)) {
        return {
          ...slab,
          disabled: !toggles[slab.name]
        }
      }

      return slab
    })

    return {
      ...updated,
      slabs
    }
  }
}

// Add given load case to final composite tree
function addLoadCase(loadCase: EditableLoadCase) {
  return function(kase: EditableCase): EditableCase {
    return {
      ...kase,
      loadCases: [...kase.loadCases, loadCase],
      touchedCategories: R.union(kase.touchedCategories, ['loads']),
      activeItem: loadCase.id
    }
  }
}

function touch(category: string | undefined, categories: string[]): string[] {
  return R.union<string>(categories, category ? [category] : [])
}

function selectNextItem() {
  // tslint:disable-next-line
  const ids: (list: any) => string[] = R.pluck('id')
  return function(kase: EditableCase): EditableCase {
    // To select next item, we pull a list of all IDs we currently have and just
    // move forward in that list
    const selectables: string[] = [
      ...ids(kase.beams),
      ...ids(kase.slabs),
      ...ids(kase.loadCases.filter(R.propEq('auto', false)))
    ]

    if (!kase.activeItem) {
      return { ...kase, activeItem: selectables[0] }
    }

    const currentIdx = selectables.indexOf(kase.activeItem)

    if (currentIdx === -1 || currentIdx + 1 >= selectables.length) {
      return { ...kase, activeItem: selectables[0] }
    }

    const activeItem = selectables[currentIdx + 1]
    const activeCategory = findActiveCategory(kase, selectables[currentIdx])
    return {
      ...kase,
      activeItem,
      touchedCategories: touch(activeCategory, kase.touchedCategories)
    }
  }
}

function deleteLoadCase(loadCaseId: string) {
  return function(kase: EditableCase): EditableCase {
    return {
      ...kase,
      loadCases: R.reject(R.propEq('id', loadCaseId), kase.loadCases),
      touchedCategories: R.union(kase.touchedCategories, ['loads']),
      activeItem: loadCaseId === kase.activeItem ? null : kase.activeItem
    }
  }
}

// Find a load case by id and modify it. This lets us write much simpler editing functions
function adjustLoadCase(
  loadCaseId: string,
  adjustFn: (loadCase: EditableLoadCase) => EditableLoadCase
) {
  return function(kase: EditableCase): EditableCase {
    const idx = R.findIndex(R.propEq('id', loadCaseId), kase.loadCases)

    if (idx === -1) {
      return kase
    }

    return {
      ...kase,
      loadCases: R.adjust(adjustFn, idx, kase.loadCases)
    }
  }
}

// Update a load case.
function updateLoadCase(loadCase: EditableLoadCase) {
  // We don't really want to change the load case, we just override it.
  return adjustLoadCase(loadCase.id, () => loadCase)
}

// Add a load to existing load case
function addLoad(loadCaseId: string) {
  return adjustLoadCase(loadCaseId, lc => {
    const loads = [
      ...lc.loads,
      { name: 'New load', values: [[0, 0]], actsOn: [], type: 'area' }
    ]

    return { ...lc, loads, activeLoad: loads.length - 1 }
  })
}

function updateLoad(loadCaseId: string, index: number, load: EditableLoad) {
  return adjustLoadCase(loadCaseId, lc => {
    if (index < 0 || index >= lc.loads.length) {
      return lc
    }

    return { ...lc, loads: R.update(index, load, lc.loads) }
  })
}

function deleteLoad(loadCaseId: string, index: number) {
  return adjustLoadCase(loadCaseId, lc => {
    if (index < 0 || index >= lc.loads.length) {
      return lc
    }

    return { ...lc, loads: R.remove(index, 1, lc.loads) }
  })
}

// Select a load inside a load case
function selectLoad(loadCaseId: string, idx: number) {
  return adjustLoadCase(loadCaseId, lc => {
    if (idx < 0) {
      return lc
    }

    return { ...lc, activeLoad: idx }
  })
}

// Perform an update to currently active case
function updateActiveCase(
  state: ProjectState,
  updateFn: (kase: EditableCase) => EditableCase,
  updateDirty: boolean = true
): ProjectState {
  const caseIndex = R.findIndex(R.propEq('id', state.activeCase), state.cases)

  if (caseIndex === -1) {
    return state
  }

  return {
    ...state,
    dirty: updateDirty ? true : state.dirty,
    cases: R.update(caseIndex, updateFn(state.cases[caseIndex]), state.cases)
  }
}

function firstActiveCase(cases: EditableCase[]): EditableCase | undefined {
  return cases.find(kase => !kase._deleted)
}

function currentBeamType(state: ProjectState): string {
  if (!state.activeCase) {
    return '<none>'
  }

  const kase = R.find<EditableCase>(
    R.propEq('id', state.activeCase),
    state.cases
  )
  if (!kase || !kase.beams[0]) {
    return '<none>'
  }

  return kase.beams[0].profileType || 'D'
}

// Select the first item from given category if the current selection is from a different
//   one.
function selectFirst(category: string, key: 'slabs' | 'beams' | 'loadCases') {
  return function(kase: EditableCase) {
    const activeCategory = findActiveCategory(kase, kase.activeItem)
    if (activeCategory === category) {
      return kase
    }

    return {
      ...kase,
      touchedCategories: R.union(kase.touchedCategories, [category]),
      activeItem: kase[key][0].id
    }
  }
}

/*

  The actual reducer

*/

const reducer = (
  state: ProjectState = initialState,
  action: Action | LocationChangeAction
): ProjectState => {
  switch (action.type) {
    case 'SELECT_COUNTRY':
      return {
        ...updateActiveCase(
          state,
          kase => ({
            ...kase,
            spaceType: getSpaceType(action.payload, kase.spaceType)
          }),
          false
        ),
        location: action.payload,
        defaultSpaceType: getDefaultSpaceType(action.payload)
      }

    case 'SELECT_SPACE_TYPE':
      return {
        ...updateActiveCase(
          state,
          kase => ({
            ...kase,
            spaceType: action.payload
          }),
          false
        ),
        defaultSpaceType: action.payload
      }

    case 'CASE_SELECT_ITEM': {
      return updateActiveCase(
        state,
        (kase: EditableCase) => {
          const currentCategory = findActiveCategory(kase, action.payload)
          return {
            ...kase,
            touchedCategories: touch(currentCategory, kase.touchedCategories),
            activeItem: action.payload
          }
        },
        false
      )
    }

    case 'CASE_SELECT_NEXT_ITEM': {
      return updateActiveCase(state, selectNextItem())
    }

    case 'CASE_SELECT_FIRST_BEAM': {
      return updateActiveCase(state, selectFirst('beams', 'beams'))
    }

    case 'CASE_SELECT_FIRST_SLAB': {
      return updateActiveCase(state, selectFirst('slabs', 'slabs'))
    }

    case 'CASE_SELECT_FIRST_LOAD_CASE': {
      return updateActiveCase(state, selectFirst('loads', 'loadCases'))
    }

    case 'UPDATE_BEAM': {
      const activeBeamType = currentBeamType(state)

      if (action.payload.profileType === 'DR' && activeBeamType === 'D') {
        // Changing from D -> DR
        return updateActiveCase(
          state,
          updateBeamAndToggleSlabs(action.payload, { left: true, right: false })
        )
      } else if (
        action.payload.profileType === 'D' &&
        activeBeamType === 'DR'
      ) {
        /* When going back to D from DR, re-enable slabs */
        return updateActiveCase(
          state,
          updateBeamAndToggleSlabs(action.payload, { left: true, right: true })
        )
      } else {
        return updateActiveCase(state, updateBeam(action.payload))
      }
    }

    case 'UPDATE_SLAB':
      return updateActiveCase(state, updateSlab(action.payload))

    case 'TOGGLE_OTHER_SLABS':
      return updateActiveCase(state, kase => ({
        ...kase,
        slabs: kase.slabs.map(slab => ({
          ...slab,
          disabled:
            slab.id !== action.payload.slabId
              ? action.payload.value
              : slab.disabled
        }))
      }))

    case 'UPDATE_LOAD_CASE':
      return updateActiveCase(state, updateLoadCase(action.payload))

    case 'UPDATE_LOAD':
      return updateActiveCase(
        state,
        updateLoad(
          action.payload.loadCaseId,
          action.payload.index,
          action.payload.load
        )
      )

    case 'DELETE_LOAD':
      return updateActiveCase(
        state,
        deleteLoad(action.payload.loadCaseId, action.payload.index)
      )

    case 'ADD_LOAD_CASE':
      return updateActiveCase(state, addLoadCase(action.payload))

    case 'DELETE_LOAD_CASE':
      return updateActiveCase(state, deleteLoadCase(action.payload))

    case 'ADD_LOAD':
      return updateActiveCase(state, addLoad(action.payload.loadCaseId))

    case 'SELECT_LOAD':
      return updateActiveCase(
        state,
        selectLoad(action.payload.loadCaseId, action.payload.index)
      )

    case 'SAVE_TEMP_THUMBNAIL':
      return { ...state, thumbnailData: action.payload }

    case 'LOAD_DEFAULT_VALUES': {
      const { values, spaceType } = action.payload
      const beams = values.beams
      return updateActiveCase(
        state,
        (kase: EditableCase) => {
          return {
            ...kase,
            spaceType,
            loadCases: values.loadCases,
            beams: beams,
            slabs: values.slabs,
            touchedCategories: [...DefaultTouchedCategories, 'beams'],
            activeItem: beams.length > 0 ? beams[0].id : null
          }
        },
        false
      )
    }

    case 'INITIALIZE_PROJECT': {
      const beams = action.payload.beams
      return {
        projectId: 'new',
        new: true,
        dirty: false,
        name: 'Untitled',
        description: '',
        location: state.location,
        defaultSpaceType: getDefaultSpaceType(state.location),
        defaultFireResistance: state.defaultFireResistance,
        defaultConcreteGrade: state.defaultConcreteGrade,

        cases: [
          {
            id: 'new_case',
            name: 'New case',
            description: '',
            spaceType: state.defaultSpaceType,
            loadCases: action.payload.loadCases,
            beams: beams,
            slabs: action.payload.slabs,
            _new: true,
            touchedCategories: [...DefaultTouchedCategories, 'beams'],
            activeItem: beams.length > 0 ? beams[0].id : null
          }
        ],
        activeCase: 'new_case',
        thumbnailData: ''
      }
    }

    case LOCATION_CHANGE:
      if (action.payload.pathname === '/location') {
        return initialState
      } else {
        return state
      }

    case 'UNLOAD_PROJECT':
      return initialState

    case 'LOAD_PROJECT': {
      if (action.error) {
        return { ...state, projectError: action.payload.message }
      }

      const cases = action.payload.cases

      let activeCase: string | null = null
      if (action.payload.activeCase) {
        activeCase = action.payload.activeCase
      } else if (cases.length >= 0) {
        activeCase = cases[0].id
      }

      return {
        ...state,
        activeCase,
        projectId: action.payload.projectId,
        name: action.payload.name,
        location: action.payload.location,
        defaultSpaceType: action.payload.spaceType,
        defaultFireResistance: action.payload.defaultFireResistance,
        defaultConcreteGrade: action.payload.defaultConcreteType,
        dirty: false,
        new: false,
        cases
      }
    }

    case 'RESET_PROJECT_DIRTY':
      return { ...state, dirty: false }

    case 'CREATE_CASE': {
      const kase: EditableCase = {
        description: '',
        spaceType: state.defaultSpaceType,
        touchedCategories: DefaultTouchedCategories,
        _new: true,
        ...action.payload
      }

      return {
        ...state,
        cases: [...state.cases, kase],
        activeCase: kase.id
      }
    }

    case 'DELETE_CASE': {
      // Make sure we have at least one case for now
      if (state.cases.length === 1) {
        return state
      }

      const idx = R.findIndex(R.propEq('id', action.payload), state.cases)

      if (idx === -1) {
        return state
      }

      const cases = R.adjust<EditableCase>(
        R.assoc('_deleted', true),
        idx,
        state.cases
      )

      return {
        ...state,
        cases,
        activeCase:
          state.activeCase === action.payload
            ? firstActiveCase(cases)!.id
            : state.activeCase
      }
    }

    case 'SELECT_CASE':
      return { ...state, activeCase: action.payload }

    case 'USER_LOGOUT':
      return initialState

    default:
      return state
  }
}

export default reducer



// WEBPACK FOOTER //
// ./src/reducers/project.ts